/**
 * Очередь синхронизации подходов.
 *
 * Запись подхода никогда не ждёт сеть: она мгновенно ложится в IndexedDB и
 * возвращает управление интерфейсу. Отправкой занимается фоновый прогон.
 * Это и есть то, что делает «логирование в 2 тапа» (продуктовый принцип №2)
 * честным обещанием, а не обещанием при хорошем интернете.
 *
 * Разница между «сеть не дошла» и «сервер отказал» здесь принципиальная:
 * первое — повторяем вечно, второе — помечаем как сбой и показываем человеку.
 * Бесконечный повтор запроса, который сервер уже отверг по существу
 * (например, RPE 11), не починится никогда и только сожжёт батарею.
 */

import { ApiError, NetworkError, api } from '../api/client'
import type { SetInput, WorkoutSet } from '../api/types'
import { dequeue, enqueue, queuedItems, type QueuedSet } from './db'

type Listener = (state: QueueState) => void

export interface QueueState {
  pending: number
  failed: number
  syncing: boolean
  online: boolean
}

const listeners = new Set<Listener>()

/**
 * Замок очереди. Пока он взят, очередь либо отправляется (`flush`), либо
 * правится (`amendQueued`, `removeQueued`) — но не то и другое сразу.
 *
 * Без него правка гонялась бы с отправкой: прогон читает подход, шлёт его на
 * сервер и после ответа удаляет запись из очереди. Поправь человек подход в
 * эту секунду — на сервер ушли бы старые цифры, а исправленная запись была бы
 * удалена как «уже отправленная». Экран показывал бы одно, база хранила
 * другое, и никто бы об этом не узнал.
 */
let syncing = false
/** Текущий прогон — чтобы правка могла дождаться его конца. */
let running: Promise<void> = Promise.resolve()

export function newUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  // Древние webview без randomUUID: важна не криптостойкость, а уникальность
  // в пределах одного устройства.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  void notify()
  return () => listeners.delete(listener)
}

async function notify(): Promise<void> {
  const items = await queuedItems()
  const state: QueueState = {
    pending: items.filter((i) => i.status === 'pending').length,
    failed: items.filter((i) => i.status === 'failed').length,
    syncing,
    online: navigator.onLine,
  }
  listeners.forEach((l) => l(state))
}

/**
 * Записать подход. Возвращается сразу, до похода в сеть.
 *
 * `client_uuid` генерируется здесь и остаётся неизменным при всех повторах —
 * на нём держится идемпотентность на стороне сервера.
 */
export async function recordSet(payload: SetInput): Promise<QueuedSet> {
  const uuid = payload.client_uuid ?? newUuid()
  const item: QueuedSet = {
    uuid,
    payload: { ...payload, client_uuid: uuid },
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
  }
  await enqueue(item)
  void notify()
  // Отправку не ждём: интерфейс уже нарисовал подход как записанный.
  void flush()
  return item
}

/**
 * Прогнать очередь. Безопасно звать сколько угодно раз: повторный вызов
 * во время работы возвращает уже идущий прогон.
 */
export function flush(): Promise<void> {
  if (syncing) return running
  syncing = true
  void notify()
  running = send().finally(() => {
    syncing = false
    void notify()
  })
  return running
}

async function send(): Promise<void> {
  for (const item of await queuedItems()) {
    if (item.status !== 'pending') continue
    try {
      await api.post<WorkoutSet>('/sets', item.payload)
      await dequeue(item.uuid)
    } catch (error) {
      if (error instanceof NetworkError) {
        // Связи нет — оставляем в очереди и прекращаем прогон: остальные
        // элементы упрутся в ту же стену, незачем их трогать.
        await enqueue({ ...item, attempts: item.attempts + 1 })
        break
      }
      if (error instanceof ApiError && error.isAuth) {
        // Сессия мертва. Данные не теряем: после входа прогон повторится.
        break
      }
      if (error instanceof ApiError && error.status >= 500) {
        // Сбой НА СТОРОНЕ СЕРВЕРА, а не отказ по существу: рестарт при
        // деплое, 502 от nginx, упавший запрос. Пометить такой подход
        // «не принят» значит объявить факт спорным из-за чужой аварии и
        // потребовать от человека ручного повтора. Оставляем в очереди и
        // прекращаем прогон — остальные упрутся в ту же стену.
        await enqueue({ ...item, attempts: item.attempts + 1 })
        break
      }
      if (error instanceof ApiError) {
        await enqueue({
          ...item,
          attempts: item.attempts + 1,
          status: 'failed',
          lastError: error.message,
        })
        continue
      }
      throw error
    }
  }
}

/** Взять замок очереди, дождавшись конца идущего прогона. */
async function locked<T>(work: () => Promise<T>): Promise<T> {
  // Цикл, а не одно ожидание: пока мы ждали, замок мог взять кто-то ещё.
  while (syncing) await running.catch(() => undefined)
  syncing = true
  // `running` обязан быть НЕзавершённым обещанием, пока замок взят: ожидание
  // уже выполненного крутилось бы в микрозадачах и не давало бы IndexedDB
  // ответить — то есть держателю замка его отпустить.
  let release: () => void = () => undefined
  running = new Promise<void>((resolve) => {
    release = resolve
  })
  try {
    return await work()
  } finally {
    syncing = false
    release()
    void notify()
  }
}

/** Что человек может поправить в уже записанном подходе. */
export type SetChanges = Partial<Pick<SetInput, 'weight' | 'reps' | 'rpe'>>

/**
 * Поправить подход, который ещё лежит в очереди.
 *
 * `false` — в очереди его уже нет: он уехал на сервер, и править его нужно
 * там (`PATCH /sets/{id}`). Ответ честен именно благодаря замку: к моменту
 * проверки идущая отправка уже закончилась, и подход либо здесь, либо там.
 *
 * Сбойная запись после правки снова становится ожидающей: человек мог
 * исправить ровно то, за что сервер её отверг.
 */
export async function amendQueued(uuid: string, changes: SetChanges): Promise<boolean> {
  const amended = await locked(async () => {
    const item = (await queuedItems()).find((i) => i.uuid === uuid)
    if (!item) return false
    await enqueue({
      ...item,
      payload: { ...item.payload, ...changes },
      status: 'pending',
      lastError: undefined,
    })
    return true
  })
  if (amended) void flush()
  return amended
}

/**
 * Убрать из очереди подход, записанный по ошибке. `false` — его там уже нет:
 * он на сервере, и удалять его нужно там.
 */
export async function removeQueued(uuid: string): Promise<boolean> {
  return locked(async () => {
    const found = (await queuedItems()).some((i) => i.uuid === uuid)
    if (found) await dequeue(uuid)
    return found
  })
}

/** Повторить отправку записей, помеченных как сбойные. */
export async function retryFailed(): Promise<void> {
  for (const item of await queuedItems()) {
    if (item.status === 'failed') {
      await enqueue({ ...item, status: 'pending', lastError: undefined })
    }
  }
  await flush()
}

/** Выбросить сбойную запись — единственный способ убрать её из очереди. */
export async function discard(uuid: string): Promise<void> {
  await dequeue(uuid)
  void notify()
}

/**
 * Повесить прогон на события, после которых связь обычно появляется.
 *
 * `visibilitychange` важнее, чем кажется: телефон в кармане между подходами
 * усыпляет вкладку, и `online` может не прийти вовсе.
 */
export function startAutoSync(): () => void {
  const run = () => void flush()

  window.addEventListener('online', run)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') run()
  })
  const timer = window.setInterval(run, 30_000)
  run()

  return () => {
    window.removeEventListener('online', run)
    window.clearInterval(timer)
  }
}
